import { NodeType, type StreamType, type WorkflowDocument, type WorkflowEdge, type WorkflowNode } from '../nodes/NodeTypes';
import { edgeStream } from './workflowGraph';

export type ShellTarget = 'bash' | 'powershell';

export interface CommandBuildResult {
  command: string;
  filterGraph: string;
  errors: string[];
  warnings: string[];
}

function streamCode(streamType: StreamType): 'v' | 'a' {
  return streamType === 'video' ? 'v' : 'a';
}

function shellQuote(value: string, shell: ShellTarget): string {
  if (shell === 'powershell') return "'" + value.replace(/'/g, "''") + "'";
  return "'" + value.replace(/'/g, "'\\''") + "'";
}

function escapeFilterValue(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/([,:;\[\]'])/g, '\\$1');
}

function renderTemplate(template: string, params: Record<string, string>, errors: string[], label: string, filter = false): string {
  const rendered = template.replace(/\{([a-zA-Z0-9_]+)\}/g, (_match, key: string) => {
    const value = params[key];
    if (value === undefined || value.trim() === '') {
      errors.push(`${label} 缺少“${key}”参数。`);
      return '';
    }
    return filter ? escapeFilterValue(value.trim()) : value.trim();
  });
  if (/\{[^}]+\}/.test(rendered)) errors.push(`${label} 包含无法识别的参数占位符。`);
  if (!rendered.trim()) errors.push(`${label} 没有可用的 FFmpeg 表达式。`);
  return rendered.trim();
}

function topologicalOrder(nodes: WorkflowNode[], edges: WorkflowEdge[]): WorkflowNode[] | null {
  const degree = new Map(nodes.map((node) => [node.id, 0]));
  edges.forEach((edge) => {
    if (degree.has(edge.source) && degree.has(edge.target)) degree.set(edge.target, (degree.get(edge.target) || 0) + 1);
  });
  const ready = nodes.filter((node) => degree.get(node.id) === 0);
  const ordered: WorkflowNode[] = [];
  while (ready.length) {
    const node = ready.shift() as WorkflowNode;
    ordered.push(node);
    edges.filter((edge) => edge.source === node.id).forEach((edge) => {
      const nextDegree = (degree.get(edge.target) || 0) - 1;
      degree.set(edge.target, nextDegree);
      if (nextDegree === 0) {
        const nextNode = nodes.find((item) => item.id === edge.target);
        if (nextNode) ready.push(nextNode);
      }
    });
  }
  return ordered.length === nodes.length ? ordered : null;
}

function upstreamIds(output: WorkflowNode, edges: WorkflowEdge[]): Set<string> {
  const result = new Set<string>();
  const queue = [output.id];
  while (queue.length) {
    const target = queue.pop() as string;
    edges.filter((edge) => edge.target === target).forEach((edge) => {
      if (!result.has(edge.source)) {
        result.add(edge.source);
        queue.push(edge.source);
      }
    });
  }
  return result;
}

function sourceLabel(
  source: WorkflowNode,
  streamType: StreamType,
  inputIndexes: Map<string, number>,
  labels: Map<string, string>,
): string | undefined {
  if (source.type === NodeType.INPUT) {
    const index = inputIndexes.get(source.id);
    return index === undefined ? undefined : `${index}:${streamCode(streamType)}`;
  }
  return labels.get(`${source.id}:${streamType}`);
}

export function buildFFmpegCommand(workflow: WorkflowDocument, shell: ShellTarget = 'bash'): CommandBuildResult {
  const errors: string[] = [];
  const warnings: string[] = [];
  const nodes = workflow.nodes;
  const edges = workflow.edges.filter((edge) => nodes.some((node) => node.id === edge.source) && nodes.some((node) => node.id === edge.target));
  const inputNodes = nodes.filter((node) => node.type === NodeType.INPUT);
  const outputNodes = nodes.filter((node) => node.type === NodeType.OUTPUT);
  if (!inputNodes.length) errors.push('请先添加至少一个输入文件。');
  if (!outputNodes.length) errors.push('请添加输出节点并设置输出文件名。');
  inputNodes.forEach((node, index) => {
    if (!(node.data.params?.filename || '').trim()) errors.push(`输入 ${index + 1} 缺少媒体路径。`);
  });
  outputNodes.forEach((node, index) => {
    if (!(node.data.params?.filename || '').trim()) errors.push(`输出 ${index + 1} 缺少文件名。`);
  });
  const ordered = topologicalOrder(nodes, edges);
  if (!ordered) errors.push('工作流中存在循环连接，请移除造成循环的连线。');
  if (errors.length) return { command: '', filterGraph: '', errors, warnings };

  const orderedNodes = ordered as WorkflowNode[];
  const inputIndexes = new Map(inputNodes.map((node, index) => [node.id, index]));
  const labels = new Map<string, string>();
  const graphParts: string[] = [];
  const outputAncestors = new Set(outputNodes.flatMap((output) => Array.from(upstreamIds(output, edges))));
  const connectedFilters = new Set(orderedNodes
    .filter((node) => node.type === NodeType.PROCESSING && node.data.operation !== 'option' && outputAncestors.has(node.id))
    .map((node) => node.id));
  const ignoredFilters = orderedNodes.filter((node) => node.type === NodeType.PROCESSING && node.data.operation !== 'option' && !connectedFilters.has(node.id));
  if (ignoredFilters.length) warnings.push(`${ignoredFilters.length} 个滤镜未连接到输出，已从命令中忽略。`);

  let videoLabelIndex = 0;
  let audioLabelIndex = 0;
  orderedNodes.forEach((node) => {
    if (node.type !== NodeType.PROCESSING || node.data.operation === 'option' || !connectedFilters.has(node.id)) return;
    const streamType: StreamType = node.data.streamType || (node.data.isAudio ? 'audio' : 'video');
    const incoming = edges.filter((edge) => edge.target === node.id && edgeStream(edge, nodes) === streamType)
      .sort((a, b) => (a.targetHandle === 'secondary' ? 1 : 0) - (b.targetHandle === 'secondary' ? 1 : 0));
    const inputLabels = incoming.map((edge) => {
      const source = nodes.find((item) => item.id === edge.source);
      return source ? sourceLabel(source, streamType, inputIndexes, labels) : undefined;
    }).filter((label): label is string => !!label);
    const requiredInputs = Math.max(1, node.data.inputCount || 1);
    if (inputLabels.length < requiredInputs) {
      errors.push(`${node.data.label} 需要 ${requiredInputs} 路${streamType === 'video' ? '视频' : '音频'}输入，当前连接 ${inputLabels.length} 路。`);
      return;
    }
    const expression = renderTemplate(node.data.command || '', node.data.params || {}, errors, node.data.label, true);
    const nextLabel = streamType === 'video' ? `v${videoLabelIndex++}` : `a${audioLabelIndex++}`;
    graphParts.push(inputLabels.map((label) => `[${label}]`).join('') + expression + `[${nextLabel}]`);
    labels.set(`${node.id}:${streamType}`, nextLabel);
  });

  const filterGraph = graphParts.join(';');
  const commandParts = ['ffmpeg', '-hide_banner'];
  inputNodes.forEach((node) => {
    const params = node.data.params || {};
    if (params.seek?.trim()) commandParts.push('-ss', shellQuote(params.seek.trim(), shell));
    if (params.duration?.trim()) commandParts.push('-t', shellQuote(params.duration.trim(), shell));
    commandParts.push('-i', shellQuote(params.filename.trim(), shell));
  });

  if (filterGraph) commandParts.push('-filter_complex', shellQuote(filterGraph, shell));

  const optionNodes = nodes.filter((node) => node.type === NodeType.PROCESSING && node.data.operation === 'option');
  outputNodes.forEach((output, outputIndex) => {
    const outputParams = output.data.params || {};
    const outputEdges = edges.filter((edge) => edge.target === output.id);
    (['video', 'audio'] as StreamType[]).forEach((streamType) => {
      if (streamType === 'video' && outputParams.disableVideo === 'true') {
        commandParts.push('-vn');
        return;
      }
      if (streamType === 'audio' && outputParams.disableAudio === 'true') {
        commandParts.push('-an');
        return;
      }
      const streamEdges = outputEdges.filter((edge) => edgeStream(edge, nodes) === streamType);
      const mapped = streamEdges.map((edge) => {
        const source = nodes.find((node) => node.id === edge.source);
        return source ? sourceLabel(source, streamType, inputIndexes, labels) : undefined;
      }).filter((label): label is string => !!label);
      const unique = Array.from(new Set(mapped));
      if (unique.length) unique.forEach((label) => commandParts.push('-map', shellQuote(label.includes(':') ? `${label}?` : `[${label}]`, shell)));
      else commandParts.push('-map', shellQuote(`0:${streamCode(streamType)}?`, shell));
    });

    const videoCodec = (outputParams.videoCodec || 'libx264').trim();
    const audioCodec = (outputParams.audioCodec || 'aac').trim();
    if (videoCodec) commandParts.push('-c:v', videoCodec);
    if (videoCodec !== 'copy' && outputParams.quality?.trim()) {
      const quality = Number(outputParams.quality);
      if (!Number.isFinite(quality) || quality < 0 || quality > 51) errors.push(`输出 ${outputIndex + 1} 的 CRF 质量范围应为 0–51。`);
      else commandParts.push('-crf', String(quality));
    }
    if (outputParams.preset?.trim() && ['libx264', 'libx265', 'libx264rgb'].includes(videoCodec)) {
      commandParts.push('-preset', outputParams.preset.trim());
    }
    if (audioCodec) commandParts.push('-c:a', audioCodec);
    if (outputParams.audioBitrate?.trim() && audioCodec !== 'copy') commandParts.push('-b:a', outputParams.audioBitrate.trim());
    if (outputParams.frameRate?.trim()) commandParts.push('-r', outputParams.frameRate.trim());
    if (outputParams.pixelFormat?.trim()) commandParts.push('-pix_fmt', outputParams.pixelFormat.trim());
    if (outputParams.fastStart === 'true') commandParts.push('-movflags', '+faststart');
    commandParts.push(shellQuote(outputParams.filename.trim(), shell));
  });

  optionNodes.forEach((node) => {
    const option = renderTemplate(node.data.command || '', node.data.params || {}, errors, node.data.label);
    option.split(/\s+/).filter(Boolean).forEach((part, index) => {
      commandParts.push(index === 0 || part.startsWith('-') ? part : shellQuote(part, shell));
    });
  });

  return { command: errors.length ? '' : commandParts.join(' '), filterGraph, errors, warnings };
}
